<cs-header>
  <cs-header-title>{{ title }}</cs-header-title>
</cs-header>

<!-- data-e2e-skipped-meal-restored marks that a Restore tap on the notice below was accepted and
     the meal is no longer skipped. It is what the E2E test waits for after tapping Restore, so a
     tap the browser loses is tapped again instead of reading as a tap that did nothing -- see
     skippedMealRestored in edit-meal.ts.
     data-e2e-meal-id names the open meal, so an E2E test can find it in the database. Angular's
     window.ng would answer that too, but no deployed build has it (#6212). -->
<cs-content [attr.data-e2e-skipped-meal-restored]="skippedMealRestored ? '' : null"
  [attr.data-e2e-meal-id]="meal?._id">
  <!-- df-a5fbe964df94: a skipped meal's food is left out of the day's totals, and adding food
       here does not un-skip it. Say so, and offer the same Restore the day list offers. -->
  @if (isMealSkipped) {
    <cs-info-card id="skippedMealNotice" type="warning" [message]="LL.editMeal.skippedNotice()"
      [actionText]="LL.common.buttons.restoreTask()" [dismissible]="false"
      (actionClick)="restoreSkippedMealAsync()">
    </cs-info-card>
  }

  <!-- A one-glance summary of what is in the meal, for a meal that already holds something
       (#4969). The same five-across card the portion screen uses, in its compact form. It is
       now the ONLY macro card on this screen: the full card that used to sit at the foot of
       the page was removed once this one carried the same two dials, because the two said the
       same thing twice and the one at the bottom was the one nobody scrolled to. -->
  @if (showCompactMacros) {
    <cs-card-list>
      <cs-card class="meal-macros">
        <!-- the Nutrients link sits at the right end of the title row, where other cards put
             their one action (Food > Serving Sizes' "Edit", for one) -->
        <cs-card-title>
          {{ cardTitle }}
          <cs-button tertiary icon-right icon="keyboard-arrow-right" id="mealNutrientsLink"
            (click)="openNutrientList()">
            {{ LL.nutrientList.nutrients() }}
          </cs-button>
        </cs-card-title>
        <cs-card-content>
          <div class="macro-targets-content">
            <!-- The two dials share ONE row and never wrap; a label too long for its button
                 ends in "..." instead (owner decision, 2026-09-11, #5327). Their words are the
                 portion screen's, so the same dial reads the same on both screens. -->
            <div class="segment-rows">
              <!-- scope: Meal / Day -->
              <div class="segment-container">
                <cs-segment [(selection)]="selectedScope">
                  <cs-segment-button [value]="EditMealScope.Meal">
                    {{ LL.nutrientList.meal() }}
                  </cs-segment-button>
                  <cs-segment-button [value]="EditMealScope.Day">
                    {{ LL.nutrientList.day() }}
                  </cs-segment-button>
                </cs-segment>
              </div>

              <!-- mode: Total / Left -->
              <div class="segment-container">
                <cs-segment [(selection)]="selectedDisplayMode">
                  <cs-segment-button [value]="NutritionTab.Logged">
                    {{ LL.nutrientList.total() }}
                  </cs-segment-button>
                  <cs-segment-button [value]="NutritionTab.Remaining">
                    {{ LL.nutrientList.left() }}
                  </cs-segment-button>
                </cs-segment>
              </div>
            </div>

            <!-- expandable, so tapping the macros still opens the full nutrient list; its own
                 Nutrients link is off because the card title carries it -->
            <nutrient-list [nutrients]="activeDisplayNutrients" state="compact-macros"
              [barStyle]="compactMacrosBarStyle" [displayMode]="activeNutrientDisplayMode"
              [expandable]="true" [showNutrientsLink]="false">
            </nutrient-list>

            @if (userDb.macrosFlexible || userDb.calorieCycling) {
              <cs-text-alert type="info">
                @if (userDb.macrosFlexible) {
                  <help-link helpId="@help:nutrition.flexible">{{ LL.nutrition.flexibleNutritionLink() }}</help-link>
                }
                @if (userDb.macrosFlexible && userDb.calorieCycling) {
                  {{ LL.common.words.and() }}
                }
                @if (userDb.calorieCycling) {
                  <help-link helpId="@help:intake.advanced-training-techniques-calorie-cycling"
                  >{{ LL.nutrition.calorieCyclingLink() }}</help-link>
                }
                {{ userDb.macrosFlexible && userDb.calorieCycling ? LL.nutrition.enabledForMealsBoth() : LL.nutrition.enabledForMeals() }}
              </cs-text-alert>
            }
          </div>
        </cs-card-content>
      </cs-card>
    </cs-card-list>
  }

  <!-- "Add to this meal" - every route into this meal, most-used first (#4969).

       One component draws every route in both layouts: cs-bar-button, the outlined box the
       Foods screen uses for Create Food / Create Recipe / Add Macros. A meal with nothing in it
       gets the grid, where each button carries the route's full name and its description under
       the icon. A meal that already holds food gets the row, where the same button carries a
       short name instead and the row scrolls sideways.

       BOTH are always in the page, and CSS alone decides which one has any height (the `open`
       class, moving a grid row from 0fr to 1fr). That is what makes the change between them a
       movement rather than a jump, and it is deliberately all it is: an earlier version drove
       the same animation from TypeScript, adding each layout to the page at the right moment and
       waiting for the transition to finish, and it kept leaving the wrong layout on screen
       because some path that changes the meal had not been taught to tell it. Here nothing has
       to be told anything -- `routesAsGrid` is worked out from the meal's contents every time
       this template renders, and the browser animates whatever that turns out to be.

       Two things have to be handled BECAUSE both are always drawn, and neither is optional:

       - `inert` on the closed one. A box with no height is still tab-focusable and still read
         out by a screen reader, so without this the folded-away layout is reachable by everyone
         not using a mouse.
       - The route ids move to whichever layout is open (`attr.id`, so null REMOVES the
         attribute). Leaving them on both would put every id in the document twice, which is
         invalid, makes an id-addressed click land on whichever copy comes first, and would
         quietly turn the end-to-end tests' "did my tap land" checks into ones that always
         pass. -->
  <div class="add-to-meal-swap" [class.open]="routesAsGrid" [attr.inert]="routesAsGrid ? null : ''">
    <div class="add-to-meal-swap-inner">
      <div class="add-to-meal-grid-block">
        <span class="section-title">{{ LL.editMeal.addToMeal.title() }}</span>
        <div class="add-to-meal-grid">
          @for (row of addToMealRows; track row.id) {
            <cs-bar-button class="route-tile" [attr.id]="routesAsGrid ? row.id : null"
              [class.emphasized]="row.emphasized" [icon]="row.icon" [label]="row.title"
              [description]="row.description" [ariaLabel]="row.title"
              (click)="onRouteTapped(row)"></cs-bar-button>
          }
        </div>
      </div>
    </div>
  </div>
  <div class="add-to-meal-swap" [class.open]="!routesAsGrid" [attr.inert]="routesAsGrid ? '' : null">
    <div class="add-to-meal-swap-inner">
      <!-- The row scrolls sideways and is deliberately WIDER than the space it is given, so the
           right edge always cuts a button in half. That half-button is the only thing telling
           the user the row scrolls, which is why the stylesheet sizes the buttons from the row's
           own width rather than letting them be whatever their text needs. -->
      <div class="add-to-meal-scroller">
        @for (row of addToMealRows; track row.id) {
          <!-- The drawn name is the SHORT one, because a button here is about 132px wide.
               [ariaLabel] carries the full name, so what a screen reader says is the whole route
               ("Log Macros Manually") even where the button can only draw "Macros". -->
          <cs-bar-button class="route-button" [attr.id]="routesAsGrid ? null : row.id"
            [icon]="row.icon" [label]="row.shortTitle" [ariaLabel]="row.title"
            (click)="onRouteTapped(row)"></cs-bar-button>
        }
      </div>
    </div>
  </div>

  <!-- Ingredient List (has its own cs-card-list) -->
  <!-- showAddFoodFooter is off because the add-routes block above this list already offers Add
       Foods, along with every other way into the meal (#4969). The recipe editor, which has no
       such block, keeps the footer button -- it is that screen's only way to add an ingredient. -->
  <ingredient-list #ingredientsCard [showHeader]="true" [showAddFoodFooter]="false"
    [headerTitleKey]="'ingredientSearch.titleMeal'"
    [showHeaderMenu]="true" [headerMenuItems]="ingredientListMenuItems" [ingredients]="meal.ingredients"
    [currentMealName]="meal.name" (updateNutrients)="updateTotalNutrientsHandler($event)"
    (updateIngredients)="updateIngredientsHandler($event)" (updateBasedOnFoodId)="updateBasedOnFoodIdHandler($event)"
    (findFoodHandler)="handleFindFood()" (quickMacrosHandler)="handleQuickMacros()"
    [returnCallbackName]="'onIngredientEdited'" [targets]="meal.targets"
    [dayDate]="mealDayDate" [currentMealId]="meal._id" [basedOnFoodId]="meal.basedOnFoodId">
    <ng-container ingredient-list-header-actions>
      @if (shouldShowAutoTuneButton()) {
        <cs-button icon="wand" secondary small (click)="handleAutoTune()">
          {{ LL.common.buttons.autoTune() }}
        </cs-button>
      }
    </ng-container>
  </ingredient-list>

  <!-- Recipe Instructions -->
  @if (tuningSource && $any(tuningSource).instructions) {
    <cs-card-list>
      <cs-info-card type="translucent" class="instructions-header">
        <span class="section-title">{{ LL.editMeal.instructions() }}</span>
      </cs-info-card>
      <cs-card class="recipe-info">
        <cs-card-content>
          <div class="recipe-content">
            <div class="recipe-name">{{ tuningSource.name }}</div>
            <markdown-editor [readonly]="true" [data]="$any(tuningSource).instructions"></markdown-editor>
          </div>
        </cs-card-content>
      </cs-card>
    </cs-card-list>
  }

</cs-content>
